import React, { useContext, useState } from 'react'
import {
  Box,
  Typography,
  Button,
  MenuItem,
  Select,
  FormControl,
  SelectChangeEvent,
  Grid,
  List,
  ListItem,
  CircularProgress,
  Chip
} from '@mui/material'
import { CartContext } from 'contexts/CartContext'
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag'
import FixedWidthContainer from 'components/FixedWidthContainer'
import useProduct from 'hooks/useProduct'
import { PRODUCT_IMAGE_BASE_URL } from 'config'
import { AuthContext } from 'contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import NotFoundPage from 'pages/NotFound'
import AssembleOptions from 'components/AssembleOptions'
import { useTranslation } from 'react-i18next'

const ProductDetailPage = () => {
  const { t } = useTranslation()
  const { updateCartItem } = useContext(CartContext)
  const { product, stock, stockLoading, hasLoaded } = useProduct()
  const { isAuthenticated } = useContext(AuthContext)
  const navigate = useNavigate()
  const [quantity, setQuantity] = useState(1)
  const [isAssembled, setIsAssembled] = useState(false)
  const [hingeDirection, setHingeDirection] = useState<'LEFT' | 'RIGHT'>('LEFT')

  const handleAddToCart = () => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }

    if (!product) {
      console.error('Product not found.')
      return
    }

    updateCartItem({
      productId: product.id,
      quantity,
      isAssembled,
      hingeDirection: isAssembled && product.isHingeable ? hingeDirection : null
    })
  }

  const handleQuantityChange = (event: SelectChangeEvent<number>) => {
    setQuantity(Number(event.target.value))
  }

  if (!product && hasLoaded) return <NotFoundPage />
  if (!product) return null

  return (
    <FixedWidthContainer sx={{ my: '50px' }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              width: '600px',
              height: '600px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Box
              component='img'
              src={`${PRODUCT_IMAGE_BASE_URL}${product.imageIndex}.png`}
              alt={product.sku}
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }}
            />
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            mt: '40px',
            gap: '10px'
          }}
        >
          <Typography
            variant='h2'
            sx={{ fontFamily: 'Playfair Display SC', color: 'secondary.main' }}
          >
            {product.cabinetType}
          </Typography>

          <Typography variant='h4'>SKU #: {product.sku}</Typography>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            {isNaN(product.price) ? (
              <Typography
                variant='h2'
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate('/login')}
              >
                {t('productDetail.signInToPricing')}
              </Typography>
            ) : (
              <Typography variant='h2'>
                ${Number(product.price).toFixed(2)}
              </Typography>
            )}
            {isAuthenticated && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: '36px'
                }}
              >
                {stockLoading ? (
                  <CircularProgress size={24} />
                ) : stock !== null ? (
                  stock > 0 ? (
                    <Typography variant='h3'>
                      {t('productDetail.inStock')}
                    </Typography>
                  ) : (
                    <Chip
                      label={t('productDetail.outOfStock')}
                      size='small'
                      sx={{
                        backgroundColor: 'rgba(211,47,47,0.08)',
                        color: 'error.main',
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '13px',
                        height: '28px'
                      }}
                    />
                  )
                ) : null}
              </Box>
            )}
          </Box>

          {stock !== 0 && product.afmx !== 0 && (
            <AssembleOptions
              isHingeable={product.isHingeable}
              isAssembled={isAssembled}
              hingeDirection={hingeDirection}
              onUpdate={({ isAssembled, hingeDirection }) => {
                if (isAssembled !== undefined) setIsAssembled(isAssembled)
                if (hingeDirection !== undefined)
                  setHingeDirection(hingeDirection as 'LEFT' | 'RIGHT')
              }}
            />
          )}

          {stock !== 0 && (
            <>
              <Typography variant='body1'>
                {t('productDetail.quantity')}
              </Typography>
              {stockLoading || stock === null ? (
                <Box
                  sx={{
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    mb: '10px'
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <FormControl sx={{ marginBottom: '10px' }}>
                  <Select
                    labelId='quantity-label'
                    value={quantity}
                    onChange={handleQuantityChange}
                    sx={{
                      width: '160px',
                      height: '40px',
                      borderRadius: '4px',
                      fontFamily: 'Inter',
                      fontSize: '14px',
                      fontWeight: '500',
                      lineHeight: '16.94px',
                      textAlign: 'left'
                    }}
                  >
                    {Array.from(
                      {
                        length: product.qtyRestrictions
                          ? Math.min(product.qtyRestrictions, stock)
                          : stock
                      },
                      (_, i) => i + 1
                    ).map(q => (
                      <MenuItem
                        key={q}
                        value={q}
                        sx={{ justifyContent: 'center' }}
                      >
                        <Typography
                          variant='body2'
                          sx={{ textAlign: 'center' }}
                        >
                          {q}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Button
                variant='contained'
                color='primary'
                sx={{
                  width: '160px',
                  height: '40px',
                  borderRadius: '4px',
                  background: 'primary.main',
                  marginBottom: '10px'
                }}
                onClick={handleAddToCart}
                disabled={stockLoading || stock === null}
              >
                <ShoppingBagIcon sx={{ transform: 'scale(0.8)' }} />
                {t('productDetail.addToBag')}
              </Button>
            </>
          )}

          <Typography variant='body1'>{t('productDetail.details')}</Typography>
          <List sx={{ pl: 2, py: 0 }}>
            <ListItem sx={{ position: 'relative', p: 0 }}>
              <Typography
                variant='body1'
                sx={{
                  pl: 2,
                  '&::before': {
                    content: '"•"',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'black'
                  }
                }}
              >
                {product.detail}
              </Typography>
            </ListItem>
          </List>
        </Grid>
      </Grid>
    </FixedWidthContainer>
  )
}

export default ProductDetailPage
